import React, { createContext, useContext, useEffect, useState } from 'react';
import { isFeatureEnabled } from '@/config/featureFlags';
import { useLiteMode } from '@/hooks/useLiteMode';
import { useOfflineStatus } from '@/hooks/useOfflineStatus';

interface ConnectionAwareContextType {
  isLiteMode: boolean;
  isOffline: boolean;
  isSlowConnection: boolean;
  shouldReduceAnimations: boolean;
  shouldOptimizeImages: boolean;
  connectionType: string;
  showConnectionStatus: boolean;
}

const ConnectionAwareContext = createContext<ConnectionAwareContextType | undefined>(undefined);

interface ConnectionAwareProviderProps {
  children: React.ReactNode;
}

export const ConnectionAwareProvider: React.FC<ConnectionAwareProviderProps> = ({ children }) => {
  const { isLiteMode } = useLiteMode();
  const isOffline = useOfflineStatus();
  const [connectionInfo, setConnectionInfo] = useState({
    isSlowConnection: false,
    connectionType: 'unknown'
  });

  useEffect(() => {
    // Only run if connection-aware experience is enabled
    if (!isFeatureEnabled('CONNECTION_AWARE_EXPERIENCE')) return;

    const updateConnectionInfo = () => {
      try {
        const conn = (navigator as any).connection || {};
        const isSlowConnection = conn.saveData || 
          ['slow-2g', '2g', '3g'].includes(conn.effectiveType) || 
          conn.downlink < 2;
        
        setConnectionInfo({
          isSlowConnection,
          connectionType: conn.effectiveType || 'unknown'
        });

        // Apply global connection-aware optimizations
        if (isSlowConnection || isLiteMode) {
          document.documentElement.classList.add('lite');
          
          // Reduce animations if enabled
          if (isFeatureEnabled('REDUCED_ANIMATIONS')) {
            document.documentElement.style.setProperty('--animation-duration', '0.1s');
            document.documentElement.style.setProperty('--transition-duration', '0.1s');
          }
        } else {
          document.documentElement.classList.remove('lite');
          if (isFeatureEnabled('REDUCED_ANIMATIONS')) {
            document.documentElement.style.removeProperty('--animation-duration');
            document.documentElement.style.removeProperty('--transition-duration');
          }
        }
      } catch (error) {
        console.warn('Connection detection not supported:', error);
      }
    };

    updateConnectionInfo();

    // Listen for connection changes
    if ('connection' in navigator) {
      (navigator as any).connection.addEventListener('change', updateConnectionInfo);
      return () => (navigator as any).connection.removeEventListener('change', updateConnectionInfo);
    }
  }, [isLiteMode]);

  const contextValue: ConnectionAwareContextType = {
    isLiteMode,
    isOffline,
    isSlowConnection: connectionInfo.isSlowConnection,
    shouldReduceAnimations: isFeatureEnabled('REDUCED_ANIMATIONS') && (isLiteMode || connectionInfo.isSlowConnection),
    shouldOptimizeImages: isFeatureEnabled('ADAPTIVE_IMAGE_QUALITY') && (isLiteMode || connectionInfo.isSlowConnection),
    connectionType: connectionInfo.connectionType,
    showConnectionStatus: isFeatureEnabled('LITE_MODE_BADGE')
  };

  return (
    <ConnectionAwareContext.Provider value={contextValue}>
      {children}
    </ConnectionAwareContext.Provider>
  );
};

export const useConnectionAware = (): ConnectionAwareContextType => {
  const context = useContext(ConnectionAwareContext);
  if (context === undefined) {
    throw new Error('useConnectionAware must be used within a ConnectionAwareProvider');
  }
  return context;
};